import { useState, useEffect } from "react";
import "./addExpenses.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import SearchableSelect from "../components/SearchableSelect";

function AddExpenses() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await dataService.getVendors();
        setVendors(Array.isArray(res.data.data) ? res.data.data : []);
      } catch (err) {
        console.error("Failed to fetch vendors", err);
      }
    };
    fetchVendors();
  }, []);

  const [formData, setFormData] = useState({
    expenseId: `EXP-${Math.floor(1000 + Math.random() * 9000)}`,
    date: new Date().toISOString().split('T')[0],
    category: "Office Supplies",
    amount: 0,
    vendor: "",
    description: "",
    additionalNotes: "",
    paymentMethod: "Cash",
    referenceNumber: "",
    paidFromAccount: "Business checking",
    taxAmount: 0,
    status: "Pending Approval",
    project: "",
    department: "Administration",
    employee: "",
    reimburseTo: ""
  });

  useEffect(() => {
    if (isEditMode) {
      const fetchExpense = async () => {
        try {
          const response = await dataService.getExpenses();
          const expense = response.data.data.find(e => e._id === id);
          if (expense) {
            setFormData({
              expenseId: expense.expenseId || "",
              date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : "",
              category: expense.category || "Office Supplies",
              amount: expense.amount || 0,
              vendor: expense.vendor || "",
              description: expense.description || "",
              additionalNotes: expense.additionalNotes || "",
              paymentMethod: expense.paymentMethod || "Cash",
              referenceNumber: expense.referenceNumber || "",
              paidFromAccount: expense.paidFromAccount || "Business checking",
              taxAmount: expense.taxAmount || 0,
              status: expense.status || "Pending Approval",
              project: expense.project || "",
              department: expense.department || "Administration",
              employee: expense.employee || "",
              reimburseTo: expense.reimburseTo || ""
            });
          } else {
            setError("Expense record not found");
          }
        } catch (err) {
          setError("Failed to fetch expense details");
        } finally {
          // done
        }
      };
      fetchExpense();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await dataService.updateExpense(id, formData);
        addToast("Expense updated successfully", "success");
      } else {
        await dataService.addExpense(formData);
        addToast("Expense added successfully", "success");
      }
      navigate("/expenses");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add expense");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Expense" : "Add Expense"}</h1>
        <p>{isEditMode ? `Updating ${formData.expenseId}` : "Record a new business expense"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Expense Details</h2>
              <div className="v-name">
                <div>
                  <p>Expense Number</p>
                  <input name="expenseId" value={formData.expenseId} onChange={handleChange} required placeholder="EXP-XXX" />
                </div>
                <div>
                  <p>Expense Date</p>
                  <input type="date" name="date" value={formData.date} onChange={handleChange} required />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Category</p>
                  <select name="category" value={formData.category} onChange={handleChange}>
                    <option value="Office Supplies">Office Supplies</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Rent">Rent</option>
                    <option value="Saleries & wages">Saleries & wages</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Travel">Travel</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Taxes">Taxes</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                <div>
                  <p>Amount</p>
                  <input type="number" name="amount" value={formData.amount} onChange={handleChange} required placeholder="0.00" />
                </div>
              </div>

              <div className="v-name">
                <div style={{ flex: 1 }}>
                  <SearchableSelect
                    label="Vendor/Payee"
                    name="vendor"
                    options={vendors}
                    value={formData.vendor}
                    onChange={handleChange}
                    placeholder="Select a Vendor (Optional)"
                    addPath="/vendors/add"
                    addLabel="+ New Vendor"
                  />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Description</p>
                  <input name="description" value={formData.description} onChange={handleChange} placeholder="Enter a detailed description of the expense" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Additional Notes</p>
                  <input name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} placeholder="Add any additional notes" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Payment Information</h2>
              <div className="v-name">
                <div>
                  <p>Payment method</p>
                  <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Check">Check</option>
                    <option value="Petty cash">Petty cash</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <p>Reference Number</p>
                  <input name="referenceNumber" value={formData.referenceNumber} onChange={handleChange} placeholder="XXXX-XXXX-XXXX" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Paid from Account</p>
                  <select name="paidFromAccount" value={formData.paidFromAccount} onChange={handleChange}>
                    <option value="Business checking">Business checking</option>
                    <option value="Business saving">Business saving</option>
                    <option value="company credit card">company credit card</option>
                    <option value="Petty Cash">Petty Cash</option>
                  </select>
                </div>
                <div>
                  <p>Tax Amount</p>
                  <input type="number" name="taxAmount" value={formData.taxAmount} onChange={handleChange} placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Expense Status</h2>
                <div>
                  <p>Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Draft">Draft</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Approved">Approved</option>
                    <option value="Paid">Paid</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                  <p>Currency</p>
                  <input name="currency" value="AED" disabled placeholder="AED" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Expense Tracking</h2>
                <div>
                  <p>Project</p>
                  <input name="project" value={formData.project} onChange={handleChange} placeholder="select project" />
                  <p>Department</p>
                  <select name="department" value={formData.department} onChange={handleChange}>
                    <option value="Administration">Administration</option>
                    <option value="Sales">Sales</option>
                    <option value="Operations">Operations</option>
                    <option value="IT">IT</option>
                    <option value="Human Resources">Human Resources</option>
                  </select>
                  <p>Employee</p>
                  <input name="employee" value={formData.employee} onChange={handleChange} placeholder="select employee" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Reimbursment</h2>
                <div>
                  <p>Reimburse to</p>
                  <input name="reimburseTo" value={formData.reimburseTo} onChange={handleChange} placeholder="select employee" />
                </div>
              </div>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/expenses")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Expense" : "Add Expense")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddExpenses;
